import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../../services/api';
import { getAutoToken } from '../../../utils/authUtils';

import logger from '../../../utils/logger';
const Container = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#f8f9fa',
}));

const Header = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const FormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  maxWidth: '600px',
  margin: '0 auto',
  textAlign: 'center',
}));

const AddHoliday = () => {
  const navigate = useNavigate();
  const [formState, setFormState] = useState({
    name: '',
    date: '',
    template: '',
  });
  const [templates, setTemplates] = useState([
    { value: '1', label: 'Template 1' },
    { value: '2', label: 'Template 2' },
    { value: '3', label: 'Template 3' },
    { value: '4', label: 'Template 4' },
    { value: '5', label: 'Template 5' },
  ]);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    const token = getAutoToken();
    if (!token) {
      setError('Authentication required. Please sign in again.');
      setTimeout(() => navigate('/login'), 1200);
      return;
    }
    if (!formState.name || !formState.date) {
      setError('Please provide holiday name and date.');
      return;
    }

    const selected = templates.find((t) => String(t.value) === String(formState.template));
    const payload = {
      name: formState.name.trim(),
      date: formState.date, // yyyy-mm-dd (comes from <input type="date" />)
      // Send template as human-readable string if selected; otherwise null
      template: selected ? selected.label : null,
    };
    try {
      setSubmitting(true);
      // Diagnostic logging
      logger.log('[AddHoliday] Submitting payload:', payload);
      const response = await ApiService.post('/admin/holiday/create-holiday', payload);
      // More flexible success parsing
      const msg = response?.data?.message || response?.message || response?.data?.msg || 'Holiday created successfully.';
      logger.log('[AddHoliday] Create success:', response);
      setSuccess(msg);
      setTimeout(() => navigate(-1), 800);
    } catch (e) {
      // Gather detailed error info to assist debugging
      const status = e?.status || e?.response?.status;
      const rawBody = e?.response?.data ?? e?.body ?? e;
      const serverMsg = typeof rawBody === 'string' ? rawBody : (rawBody?.message || rawBody?.error || rawBody?.msg);
      const msg = serverMsg || `Failed to create holiday${status ? ` (HTTP ${status})` : ''}. Please try again.`;
      logger.error('[AddHoliday] Create failed:', {
        status,
        error: e,
        responseData: rawBody,
        payload,
      });
      if (status === 401 || /auth(entication)?\s*failed/i.test(String(serverMsg || ''))) {
        setError('Authentication failed. Please sign in again.');
        setTimeout(() => navigate('/login'), 1200);
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const openTemplateDialog = () => {
    setNewTemplateName('');
    setIsTemplateDialogOpen(true);
  };

  const closeTemplateDialog = () => {
    setIsTemplateDialogOpen(false);
  };

  const saveNewTemplate = () => {
    const trimmed = newTemplateName.trim();
    if (!trimmed) return;
    const nextValue = String(
      templates.length
        ? Math.max(...templates.map((t) => Number(t.value) || 0)) + 1
        : 1
    );
    const newOption = { value: nextValue, label: trimmed };
    setTemplates((prev) => [...prev, newOption]);
    setFormState((prev) => ({ ...prev, template: newOption.value }));
    setIsTemplateDialogOpen(false);
  };

  return (
    <Container>
      <Header>
        <Typography variant="h4" fontWeight={700}>
          Add Holiday
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Create a holiday by entering the name, date, and template.
        </Typography>
      </Header>
      <FormContainer>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Holiday Details
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ mb: 4 }}
        >
          Fill in the information below to create a new holiday.
        </Typography>
        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        {success && (
          <Typography
            variant="body2"
            color="success.main"
            sx={{ mb: 2 }}
          >
            {success}
          </Typography>
        )}

        <TextField
          fullWidth
          label="Name"
          name="name"
          value={formState.name}
          onChange={handleChange}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Date"
          name="date"
          type="date"
          value={formState.date}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Templates</InputLabel>
          <Select
            name="template"
            value={formState.template}
            onChange={handleChange}
          >
            <MenuItem value="">
              <em>-Select-</em>
            </MenuItem>
            {templates.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          sx={{ textTransform: 'none', mb: 3 }}
          onClick={openTemplateDialog}
        >
          + Add New Template
        </Button>

        <Box>
          <Button
            variant="outlined"
            color="error"
            sx={{ mr: 2 }}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </Box>

        {/* Add Template Dialog */}
        <Dialog
          open={isTemplateDialogOpen}
          onClose={closeTemplateDialog}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Add New Template</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Template Name"
              fullWidth
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeTemplateDialog} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={saveNewTemplate}
              variant="contained"
              disabled={!newTemplateName.trim()}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </FormContainer>
    </Container>
  );
};

export default AddHoliday;
